/**
 * The page's table, in spec §4.5's order of operations. Pure.
 * 1. Creatives without a currency are dropped and counted; D1's currencyChoiceOf picks the page currency.
 * 2. Labels over every creative in that currency (labelledRowsOf).
 * 3. The channel and objective filters, then the format filter.
 * 4. The sort. 5. The first SHOW_FIRST unless "แสดงทั้งหมด" is on.
 * Filters and sorts read nothing, so none of them moves a label.
 *
 * @module services/creatives/creativeView
 */
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { isChannelShown } from '@/services/channelFilter';
import { currencyChoiceOf } from '@/services/overview/overviewFilters';
import type { ReadState } from '@/services/overview/overviewTypes';
import type { ResultMappingInfo } from '@/services/targets/resultMetric';
import { labelledRowsOf, type PricedFact } from './creativeRows';
import {
  SHOW_FIRST, type CreativeFact, type CreativeFilters, type CreativeRow, type CreativeSort, type CreativeView,
} from './creativeTypes';

type Compare = (a: CreativeRow, b: CreativeRow) => number;

/** Code-unit order: the same on every machine, whatever the locale. */
const byText = (a: string, b: string): number => (a < b ? -1 : Number(a > b));

/** Higher first; NULL after every number. */
const byNumberDesc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

/** Lower first; NULL after every number. */
const byNumberAsc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

/** Every sort's tail, and the Spend sort itself: the higher spend, then the ad name, then the ad id. */
const bySpend: Compare = (a, b) => byNumberDesc(a.spend, b.spend) || byText(a.name, b.name) || byText(a.key, b.key);

/** Cost per Result is compared within an objective only: the units differ (CPA, CPC, per 1,000). */
const OBJECTIVE_ORDER: readonly string[] = ['conversion', 'consideration', 'awareness'];
const objectiveRank = (o: string): number => (OBJECTIVE_ORDER.includes(o) ? OBJECTIVE_ORDER.indexOf(o) : OBJECTIVE_ORDER.length);

/** An n<500 row ranks as if it had no Cost per Result: last in its group, by spend. */
const rankedCpr = (r: CreativeRow): number | null => (r.lowSample ? null : r.costPerResult);
const byCpr: Compare = (a, b) =>
  objectiveRank(a.objective) - objectiveRank(b.objective) || byNumberAsc(rankedCpr(a), rankedCpr(b)) || bySpend(a, b);

/** A CTR on 500+ impressions, then n<500 rows with a CTR, then rows without one. */
const ctrTier = (r: CreativeRow): number => {
  if (r.ctr === null) return 2;
  return r.lowSample ? 1 : 0;
};
const byCtr: Compare = (a, b) => ctrTier(a) - ctrTier(b) || byNumberDesc(a.ctr, b.ctr) || bySpend(a, b);

const SORTS: Readonly<Record<CreativeSort, Compare>> = { spend: bySpend, cpr: byCpr, ctr: byCtr };

export const sortCreativeRows = (rows: readonly CreativeRow[], sort: CreativeSort): CreativeRow[] => [...rows].sort(SORTS[sort]);

/** Step 3: channel and objective, then format; "ไม่ระบุ" shows under "ทั้งหมด" only. */
const passes = (r: CreativeRow, f: CreativeFilters, channels: ReadonlySet<string>): boolean =>
  isChannelShown(channels, r.platform)
  && (f.objective === 'all' || r.objective === f.objective)
  && (f.format === 'all' || r.format === f.format);

export interface CreativeViewInput {
  facts: ReadState<CreativeFact[]>;
  /** null while the Result definition loads (R12). */
  mapping: ResultMappingInfo | null;
  filters: CreativeFilters;
  /** Bangkok's yesterday on the day the page is opened. */
  yesterday: string;
}

const NOTHING_YET: CreativeView = { money: null, unknownCurrency: 0, latestFactDate: null, table: { status: 'loading' } };

const latestOf = (facts: readonly CreativeFact[]): string | null =>
  facts.reduce<string | null>((max, f) => (max === null || f.lastDate > max ? f.lastDate : max), null);

export const buildCreativeView = ({ facts, mapping, filters, yesterday }: CreativeViewInput): CreativeView => {
  if (facts.status === 'failed') return { ...NOTHING_YET, table: { status: 'failed' } };
  if (facts.status !== 'ready') return NOTHING_YET;
  const money = currencyChoiceOf(facts.data, filters.currency);
  const head = { money, unknownCurrency: facts.data.filter((f) => f.currency === null).length, latestFactDate: latestOf(facts.data) };
  if (mapping === null) return { ...head, table: { status: 'loading' } };
  const priced = facts.data.filter((f): f is PricedFact => f.currency !== null && f.currency === money.currency);
  const channels = new Set(filters.channels);
  const rows = sortCreativeRows(labelledRowsOf(priced, mapping, yesterday).filter((r) => passes(r, filters, channels)), filters.sort);
  const shown = filters.showAll ? rows : rows.slice(0, SHOW_FIRST);
  return { ...head, table: { status: 'ready', data: { rows, shown, inCurrency: priced.length } } };
};

/** R1, D1's rule: the newest channel data-through once the freshness read is ready, else the newest creative day. */
export const creativeDataDateOf = (freshness: ChannelFreshnessState, view: Pick<CreativeView, 'latestFactDate'>): string | null => {
  if (freshness.status === 'ready') return freshness.channels.flatMap((c) => (c.dataThrough ? [c.dataThrough] : [])).sort().pop() ?? null;
  return view.latestFactDate;
};
